/**
 * Staffing vocabulary (scheduling rules plan phase D), as the API speaks it:
 * the units a worker may work and the credentials they hold with each one's
 * lapse date. Both are optional on a worker; absent means no restriction on
 * units and nothing recorded for credentials.
 */

export interface WorkerUnits {
  /** The home unit, as published shifts name it (`2-South`); null when only floats are recorded. */
  primary: string | null
  secondary: string[]
}

export interface WorkerCredential {
  /** Upper case, `BLS`. */
  code: string
  /** `YYYY-MM-DD`, the last valid day; null never lapses. */
  expiresAt: string | null
}

/** "2 South, floats to ICU, 3 West" for a detail line, each unit by `names` where it has one; "Any unit" when none are recorded. */
export function unitsText(units: WorkerUnits | null | undefined, names: ReadonlyMap<string, string> = new Map()): string {
  if (!units || (units.primary === null && units.secondary.length === 0)) {
    return 'Any unit'
  }
  const name = (unitId: string): string => names.get(unitId) ?? unitId
  const floats = units.secondary.length > 0 ? `floats to ${units.secondary.map(name).join(', ')}` : ''
  return units.primary ? [name(units.primary), floats].filter(Boolean).join(', ') : floats.replace(/^f/, 'F')
}

/** "BLS to 2027-03-01, ACLS" for a detail line; "None recorded" for none. */
export function credentialsText(credentials: readonly WorkerCredential[] | null | undefined): string {
  if (!credentials || credentials.length === 0) {
    return 'None recorded'
  }
  return credentials.map((credential) => (credential.expiresAt ? `${credential.code} to ${credential.expiresAt}` : credential.code)).join(', ')
}

/** The form's way of writing credentials, as the roster file does: "BLS:2027-03-01; ACLS". */
export function credentialsToField(credentials: readonly WorkerCredential[] | null | undefined): string {
  return (credentials ?? []).map((credential) => (credential.expiresAt ? `${credential.code}:${credential.expiresAt}` : credential.code)).join('; ')
}

/** Reads the form's credentials text; blank is none. A malformed date is passed on for the API to refuse with its reason. */
export function credentialsFromField(text: string): WorkerCredential[] {
  return text
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => {
      const [code = '', expiresAt] = part.split(':').map((piece) => piece.trim())
      return { code: code.toUpperCase(), expiresAt: expiresAt ? expiresAt : null }
    })
}

/** A list of unit ids typed as "ICU, 3-West" or "ICU; 3-West". */
export function unitListFromField(text: string): string[] {
  return [...new Set(text.split(/[;,]/).map((unit) => unit.trim()).filter((unit) => unit !== ''))]
}

/** A credential the worker holds that lapses on or before `today` plus `withinDays`. */
export function lapsingCredentials(credentials: readonly WorkerCredential[] | null | undefined, today: string, withinDays = 30): WorkerCredential[] {
  const limit = new Date(Date.parse(`${today}T00:00:00Z`) + withinDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  return (credentials ?? []).filter((credential) => credential.expiresAt !== null && credential.expiresAt <= limit)
}
